import { Handle, NodeProps, Position, useReactFlow } from 'reactflow';
import { ReactSVG } from 'react-svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { operationType } from '../../services/types';
import { Input } from './children/functions/input';
import { OpenAI } from './children/LLMs/openai';
import { DocSearch } from './children/knowledge/upload/docSearch';
import { FileUploader } from './children/knowledge/upload/files';
import { Spreadsheet } from './children/loaders/spreadsheet';
import { GoogleGemini } from './children/LLMs/google';
import { Gmail } from './children/actions/gmail';
import { Outlook } from './children/actions/outlook';
import { VideoVision } from './children/vision/videoVision';
import { OCR } from './children/vision/ocr';
import { UrlSearch } from './children/knowledge/urlSearch';
import { GoogleDrive } from './children/knowledge/googleDrive';
import { AzureStorage } from './children/knowledge/azureStorage';
import { Main } from './style';
import { Jira } from './children/actions/jira';
import { BigQuery } from './children/loaders/bigQuery';
import { Odoo } from './children/crm/odoo';
import { ClickUp } from './children/actions/clickup';
import { FlowQueue } from './children/functions/flowQueue'; 
import { Trello } from './children/tools/trello';
import { WordPress } from './children/cms/wordpress';
import { Slack } from './children/tools/slack';
import { CustomAPI } from './children/actions/customAPI';
import { PDFChunker } from './children/knowledge/pdfChunker';
import { KnowledgeBase } from './children/knowledge/knowledgeBase';
import { Webhook } from './children/knowledge/webhook';
import { Sequencer } from './children/agents/sequencer';
import { QueryDatabase } from './children/knowledge/queryDatabase';
import { Routing } from './children/functions/routing';
import { ActionsAgent } from './children/agents/actionsAgent';
import { ChatAgent } from './children/agents/chatAgent';
import { Code } from './children/functions/code';
import { AiAgent } from './children/agents/aiAgent';
import { Minimize, Settings, X, MessagesSquare } from 'lucide-react';
import { Llama } from './children/LLMs/llama';
import { handleRemoveVariablesFromBlock } from '../../services/method';
import { Start } from './children/actions/start';
import { WebSearch } from './children/knowledge/webSearch';
import { SubWorkflow } from './children/knowledge/subWorkflow';
import { SlackTrigger } from './children/triggers/slackTrigger';
import { useDispatch, useSelector } from 'react-redux';
import { setProjectData } from '../../redux/reducers/project';
import { GoogleCalendarTrigger } from './children/triggers/googleCalendarTrigger';
import { GoogleCalendar } from './children/actions/googleCalendar';
import { GoogleDriveTrigger } from './children/triggers/googleDriveTrigger';
import { MessengerSendMessage } from './children/actions/messengerSend';
import { InstagramSendMessage } from './children/actions/instagramSend';
import { MetaListenerTrigger } from './children/triggers/metaListenerTrigger';
import { RssNode } from './children/actions/rss';
import { EditFields } from './children/data/editFields';
import { TypeformTrigger } from './children/triggers/typeformTrigger';
import { TextToSpeech } from './children/vision/textToSpeech';
import { SpeechToText } from './children/vision/speechToText';
import { MergeBlock } from './children/data/merge';
import { WaitBlock } from './children/functions/wait';
import { Sql } from './children/actions/sql';
import { SqlTrigger } from './children/triggers/sqlTrigger';
import { ERPNext } from './children/crm/erpnext';
import { GoogleFormTrigger } from './children/triggers/googleFormTrigger';
import { WhatsappSendMessage } from './children/actions/whatsappSendMessage';
import { WhatsappListener } from './children/triggers/whatsappListener';
import { Notion } from './children/crm/notion';
import { OutlookTrigger } from './children/triggers/outlookTrigger';
import { TwilioSendSms } from './children/actions/twilioSendSms';
import { Airtable } from './children/databases/airtable';
import { Supabase } from './children/databases/supabase';
import { ImapTrigger } from './children/triggers/imapTrigger';
import { FacebookPosting } from './children/actions/facebookPosting';
import { InstagramPosting } from './children/actions/instagramPosting';
import { FilterBlock } from './children/flowControl/filter';
import { Shopify } from './children/actions/shopify';
import { RootState } from '../../redux/store';
import { ShopifyTrigger } from './children/triggers/shopifyTrigger';
import { WooCommerceTrigger } from './children/triggers/woocommerceTrigger';
import { WooCommerce } from './children/actions/woocommerce';

export function CustomNode(props: NodeProps<operationType>) {
   const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
   const dispatch = useDispatch();
   const isThereBlockExpanded = useSelector((state: RootState) => state.project.isThereBlockExpanded);

   // ---
   const handleMinimize = () => {
      if (typeof props.data.childOnMinimize === "function") {
         props.data.childOnMinimize();
         dispatch(setProjectData({ isThereBlockExpanded: true }));
         
      }
   }
   const handleSettings = () => {
      if (typeof props.data.childOnSettings === "function") {
         props.data.childOnSettings();
      }
   }

   // ---
   const renderContent = () => {
      const Component = {
         input: Input,
         output: Input,
         openai: OpenAI,
         googleGemini: GoogleGemini,
         docSearch: DocSearch,
         fileUploader: FileUploader,
         spreadsheet: Spreadsheet,
         gmail: Gmail,
         outlook: Outlook,
         videoVision: VideoVision,
         ocr: OCR,
         urlSearch: UrlSearch,
         googleDrive: GoogleDrive,
         azureStorage: AzureStorage,
         jira: Jira,
         bigQuery: BigQuery,
         odoo: Odoo,
         clickup: ClickUp,
         flowQueue: FlowQueue,
         trello: Trello,
         wordPress: WordPress,
         slack: Slack,
         customAPI: CustomAPI,
         pdfChunker: PDFChunker,
         knowledge: KnowledgeBase,
         webhook: Webhook,
         sequencer: Sequencer,
         queryDatabase: QueryDatabase,
         routing: Routing,
         actionsAgent: ActionsAgent,
         chatAgent: ChatAgent,
         code: Code,
         aiAgent: AiAgent,
         llama: Llama,
         start: Start,
         webSearch: WebSearch,
         subWorkflow: SubWorkflow,
         slackTrigger: SlackTrigger,
         googleCalendar: GoogleCalendar,
         googleCalendarTrigger: GoogleCalendarTrigger,
         googleDriveTrigger: GoogleDriveTrigger,
         messengerSendMessage: MessengerSendMessage,
         instaSendMessage: InstagramSendMessage,
         metaListenerTrigger: MetaListenerTrigger,
         rss: RssNode,
         editFields: EditFields,
         typeformTrigger: TypeformTrigger,
         textToSpeech: TextToSpeech,
         speechToText: SpeechToText,
         merge: MergeBlock,
         wait: WaitBlock,
         sql: Sql,
         sqlTrigger: SqlTrigger,
         erpnext: ERPNext,
         googleFormTrigger: GoogleFormTrigger,
         whatsappListener: WhatsappListener,
         whatsappSendMessage: WhatsappSendMessage,
         notion: Notion,
         outlookTrigger: OutlookTrigger,
         twilioSendSms: TwilioSendSms,
         airtable: Airtable,
         supabase: Supabase,
         imapTrigger: ImapTrigger,
         facebookPosting: FacebookPosting,
         instagramPosting: InstagramPosting,
         shopify: Shopify,
         filter: FilterBlock,
         shopifyTrigger: ShopifyTrigger,
         woocommerceTrigger: WooCommerceTrigger,
         woocommerce: WooCommerce
      }[props.data.action];
      return Component ? 
         <Component { ...props } 
            setChildMinimize={ (fn: any) => (props.data.childOnMinimize = fn) } 
            setChildSettings={(fn: any) => (props.data.childOnSettings = fn)} 
         /> 
      : null;
   };

   const isMain = props.data.action === 'input' || props.data.action === 'output';
   const isExpanded = props.data.action === 'ocr' && props.data.value?.isNoMinimize;
   const normalizedPostions: Array<{ type: 'source' | 'target'; label: string; dir: Position }> = 
      props.data.action === 'aiAgent' ? [
         { type: 'target', label: 'Input', dir: Position.Left },
         { type: 'source', label: 'Output', dir: Position.Right },
         { type: 'source', label: 'Tools', dir: Position.Right },
      ]
      : props.data.postions.map((postion) => ({
         type: postion.type as 'source' | 'target',
         label: postion.label || '',
         dir: postion.dir,
      }));

   return (<>
      <Main
         className={ props.data.action } 
         $isMinimize={ (!isMain && props.data.action !== 'ocr') || (props.data.action === 'ocr' && !props.data.value?.isNoMinimize) }
         onDoubleClick={ _=> {
            if (!isThereBlockExpanded) handleMinimize();
         }}
      >
         <div className="mainTitle">
            <div style={{ backgroundColor: props.data.background }}>{ 
               // typeof props.data.icon === 'string' ? <ReactSVG className="otherSvg" src={ props.data.icon } /> : <FontAwesomeIcon icon={ props.data.icon } />
               typeof props.data.icon === 'string' ? 
               (props.data.icon.endsWith('.png') || props.data.icon.endsWith('.jpg') 
                  ? <img src={props.data.icon} alt="" style={{ width: '32px', height: '32px', objectFit: 'contain' }} /> 
                  : <ReactSVG className="otherSvg" src={props.data.icon} />) 
                  : <FontAwesomeIcon icon={props.data.icon} />
            }</div>
            { isMain || isExpanded ? <div>
               <h5>{ props.data.name }</h5>
               <div>
                  <span className="index">
                     <span style={{ backgroundColor: props.data.background }}></span>
                     <span style={{ color: props.data.background }}>{ props.data.index }</span>
                  </span>
                  <span 
                     className="remove" 
                     onClick={ _=> handleRemoveVariablesFromBlock({ id: props.id, getNodes, getEdges, setNodes, setEdges }) }
                  >
                     <X size={ 20 } />
                  </span>
               </div>
            </div> : null }
            { !isMain ? <div className="settings">
               <span onClick={ handleMinimize }><Minimize /></span>
               <span onClick={ handleSettings } title={props.data.action === 'aiAgent' ? 'Open Chat' : 'Settings'}>
                  { props.data.action === 'aiAgent' ? <MessagesSquare /> : <Settings /> }
               </span>
            </div> : null }
         </div>
         <div 
            className="content" 
            onDoubleClick={ e => {
               if (isThereBlockExpanded) e.stopPropagation()
            }}
         >
            { renderContent() }
         </div>
      </Main>
      { normalizedPostions.map((postion, index) => (
         <Handle 
            key={ index.toString() } 
            id={ postion.label } 
            type={ postion.type } 
            position={ postion.dir } 
            className={`handle-${props.data.action} handle-child-${postion.label}`}
         >
            { postion.label ? <span className="handleTitle">{ postion.label }</span> : null }
         </Handle>
      ))}
   </>);
}